import React, { FC, useEffect, useMemo, useState } from 'react';

import * as yup from 'yup';

import BasicButton from '../../../../components/buttons/basicButton';
import TextInput from '../../../../components/inputs/textInput';
// components
import BasicModal from '../../../../components/modals/basicModal';
import styled from 'styled-components';

// constants
import aampeTheme from '../../../../constants/aampeTheme';

// helpers
import { extractLinkInfo } from '../../../../views/formulaCreate/messagePreview/email/email.helper';

interface AddLinkModalProps {
    onSubmit: (linkValue: string, textValue: string) => void;
    onCancel: () => void;
    selectedElement?: { [key: string]: any };
    isEditMode?: boolean;
}

const linkSchema = yup.string().url('Invalid URL').required('Link is required');

const AddLinkModal: FC<React.PropsWithChildren<AddLinkModalProps>> = ({
    onSubmit,
    onCancel,
    selectedElement,
    isEditMode,
}) => {
    const [error, setError] = useState<string | undefined>(undefined);
    const [textValue, setTextValue] = useState<string>('');
    const [linkValue, setLinkValue] = useState<string>('');

    useEffect(() => {
        if (selectedElement && selectedElement.children) {
            const { linkValue, textValue } = extractLinkInfo(
                selectedElement.children[0].text,
            );
            setLinkValue(linkValue);
            setTextValue(textValue);
        }
    }, [selectedElement]);

    const handleLinkEdit = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = event.target;
        setLinkValue(value);
        setError(undefined);
        linkSchema.validate(value).catch((err) => setError(err.message));
    };
    const handleTextEdit = (event: React.ChangeEvent<HTMLInputElement>) => {
        setTextValue(event.target.value);
    };

    const isDisable = useMemo<boolean>(() => {
        return !textValue || !linkValue || !!error;
    }, [textValue, linkValue, error]);

    const textError = useMemo<string | undefined>(() => {
        return !textValue ? 'Text is required' : undefined;
    }, [textValue]);

    return (
        <BasicModal
            header={'Add Link'}
            width="512px"
            headerStyle={{
                backgroundColor: aampeTheme.colors.white,
                textAlign: 'center',
                fontWeight: 500,
                fontSize: '24px',
                color: aampeTheme.colors.gray950,
                padding: aampeTheme.spacing(4, 0, 0, 0),
            }}
        >
            <ModalDescriptionText>
                The Link will be applied to all alternates of this component.
            </ModalDescriptionText>
            <Body>
                <TextInput
                    value={textValue}
                    onChange={handleTextEdit}
                    label={'Text'}
                    placeholder={'Type text here'}
                    inputStyle={{ width: '100%' }}
                    wrapperStyle={{ width: '100%' }}
                    error={textError}
                    touched
                />
                <TextInput
                    value={linkValue}
                    onChange={handleLinkEdit}
                    label={'Link'}
                    placeholder={'Type link here'}
                    inputStyle={{ width: '100%' }}
                    wrapperStyle={{ width: '100%' }}
                    error={error}
                    touched
                />
            </Body>
            <EditButtonWrapper>
                <StyledButton
                    name="Cancel"
                    action={onCancel}
                    isPrimary={false}
                />
                <StyledButton
                    name={isEditMode ? 'Save edit' : 'Save'}
                    action={() => onSubmit(linkValue, textValue)}
                    isPrimary={true}
                    disabled={isDisable}
                />
            </EditButtonWrapper>
        </BasicModal>
    );
};
export default AddLinkModal;

const EditButtonWrapper = styled.div({
    display: 'flex',
    gap: aampeTheme.spacing(2),
    margin: aampeTheme.spacing(4, 0, 2, 0),
});
const ModalDescriptionText = styled.div({
    fontWeight: 400,
    fontSize: '16px',
    color: aampeTheme.colors.neutral09,
    lineHeight: '24px',
    padding: aampeTheme.spacing(0, 1, 1, 1),
});
const StyledButton = styled(BasicButton)({
    width: '50%',
    fontSize: '16px',
    fontWeight: 500,
});
const Body = styled.div(() => ({
    display: 'flex',
    justifyContent: 'center',
    margin: aampeTheme.spacing(2, 0),
    flexDirection: 'column',
    gap: aampeTheme.spacing(2),
}));
